import { Calendar, isSchoolCalendar } from 'owa-calendar-cache';
import selectCalendar from './selectCalendar';
import { isConnectedAccount } from 'owa-accounts-store';
import { logUsage } from 'owa-analytics-actions';
import { findInArray } from 'owa-calendar-data-utils/lib/findInArray';
import { getUserMailboxInfo } from 'owa-client-ids';
import { getSelectedCalendarsForUser } from 'owa-calendar-module-selected-calendars-user-config';
import { getCalendars } from 'owa-calendar-surface-calendars-store';
import _CalendarGroup from 'owa-service/lib/contract/CalendarGroup';
import { trace } from 'owa-trace';

/**
 * load the calendar events data for the selected calendars
 * @param userIdentity user identity
 */
export async function restoreSelectedCalendars(userIdentity?: string) {
    if (!userIdentity) {
        userIdentity = getUserMailboxInfo().userIdentity;
    }

    const selectedCalendarIds = getSelectedCalendarsForUser(userIdentity);
    const allCalendars = getCalendars();
    let selectedCalendars = [];

    selectedCalendarIds?.forEach(calendarId => {
        let findResult = findInArray(allCalendars, item => {
            return calendarId == item.calculatedFolderId || calendarId == item.id.id;
        });

        if (findResult) {
            selectedCalendars.push(findResult.item);
        } else {
            trace.info(
                `[restoreSelectedCalendars] Previously selected calendar is no longer available and / or invalid: {1}: ${calendarId}`
            );
        }
    });

    const calendarLoadingPromises: Promise<void>[] = selectedCalendars.map(calendar =>
        selectCalendar(calendar, true /*DoNotPersist*/)
    );

    await Promise.all(calendarLoadingPromises);

    // log the calendar count for this account (All types of calendars for given account are accounted with this check)
    const allCalendarsForAccount = allCalendars.filter(
        c => c.id.mailboxInfo.userIdentity == userIdentity
    );

    let selectedCalendarsForAccountBucket = toCalendarCountBucket(selectedCalendars.length);
    let allCalendarsForAccountBucket = toCalendarCountBucket(allCalendarsForAccount.length);

    logUsage('SelectedCalendarsPerAccount', {
        connectedAccount_1: isConnectedAccount(userIdentity),
        // standard logging
        selectedCalendarsForAccount: selectedCalendars.length,
        allCalendarsForAccount: allCalendarsForAccount.length,
        // long term logging
        selectedCalendarsForAccount_2: `${selectedCalendarsForAccountBucket.lowerBoundResultSize} - ${selectedCalendarsForAccountBucket.upperBoundResultSize}`,
        allCalendarsForAccount_3: `${allCalendarsForAccountBucket.lowerBoundResultSize} - ${allCalendarsForAccountBucket.upperBoundResultSize}`,
    });

    const allSchoolCalendars = getSchoolCalendars(allCalendarsForAccount);
    const selectedSchoolCalendars = getSchoolCalendars(selectedCalendars);

    const selectedSchoolCalendarsBucket = toCalendarCountBucket(allSchoolCalendars.length);
    const allSchoolCalendarsBucket = toCalendarCountBucket(selectedSchoolCalendars.length);

    logUsage('SelectedSchoolCalendars', {
        // standard logging
        selectedSchoolCalendars: selectedSchoolCalendars.length,
        allSchoolCalendars: allSchoolCalendars.length,
        // long term logging
        selectedSchoolCalendars_1: `${selectedSchoolCalendarsBucket.lowerBoundResultSize} - ${selectedSchoolCalendarsBucket.upperBoundResultSize}`,
        allSchoolCalendars_2: `${allSchoolCalendarsBucket.lowerBoundResultSize} - ${allSchoolCalendarsBucket.upperBoundResultSize}`,
    });
}

function getSchoolCalendars(calendars: Calendar[]) {
    return calendars.filter(isSchoolCalendar);
}

function toCalendarCountBucket(
    numResults: number
): { lowerBoundResultSize: number | null; upperBoundResultSize: number | null } {
    if (numResults < 0) {
        // Should not be possible if result calculations are correct
        return { lowerBoundResultSize: null, upperBoundResultSize: 0 };
    } else if (numResults == 0) {
        return { lowerBoundResultSize: 0, upperBoundResultSize: 0 };
    } else if (numResults <= 3) {
        return { lowerBoundResultSize: 1, upperBoundResultSize: 3 };
    } else if (numResults <= 6) {
        return { lowerBoundResultSize: 4, upperBoundResultSize: 6 };
    } else if (numResults <= 10) {
        return { lowerBoundResultSize: 7, upperBoundResultSize: 10 };
    } else if (numResults <= 20) {
        return { lowerBoundResultSize: 11, upperBoundResultSize: 20 };
    } else {
        return { lowerBoundResultSize: 21, upperBoundResultSize: null };
    }
}
